/**
 * Workflow History Page
 * View personal workflow history with status and details
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Stack, Flex, Select, Radio, Button, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { WorkflowHistoryList } from '../components/echo/WorkflowHistoryList';
import { useEchoRunsPaginated, useEchoRunsCount } from '../api/echo';
import { useEchoUser } from '../hooks/useEchoUser';
import type { EchoRun } from '../api/types/echo';

type StatusFilter = 'all' | 'running' | 'completed' | 'failed';
type ViewMode = 'my' | 'all';

/**
 * Page for viewing workflow history
 */
export function WorkflowHistoryPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAdmin } = useEchoUser();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('my');

  // Non-admins are always scoped to their own runs; admins toggle via viewMode.
  const mine = !isAdmin || viewMode === 'my';

  const {
    runs: loadedRuns,
    loading: isLoading,
    loadingMore,
    error,
    reachedEnd,
    loadMore,
  } = useEchoRunsPaginated({ mine });
  const { data: countData } = useEchoRunsCount({ mine });
  const totalRuns = countData?.total ?? null;

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const filteredRuns = useMemo(() => {
    if (statusFilter === 'all') return loadedRuns;
    return loadedRuns.filter((run) => {
      const s = run.status?.toLowerCase() || '';
      if (statusFilter === 'running') {
        return s === 'running' || s === 'queued' || s === 'processing';
      }
      return s === statusFilter;
    });
  }, [loadedRuns, statusFilter]);

  const handleViewDetails = useCallback((run: EchoRun) => {
    if (run.s3_storage_location) {
      navigate(`/view?location=${encodeURIComponent(run.s3_storage_location)}`);
    }
  }, [navigate]);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'running', label: 'Running' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
  ];

  const countLabel = (() => {
    if (totalRuns == null) return null;
    if (statusFilter === 'all') {
      return `${loadedRuns.length} of ${totalRuns} ${totalRuns === 1 ? 'run' : 'runs'}`;
    }
    const filteredWord = filteredRuns.length === 1 ? 'match' : 'matches';
    return `${filteredRuns.length} ${filteredWord} filter (${loadedRuns.length} of ${totalRuns} loaded)`;
  })();

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>
          Workflow History
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, color: mutedColor }}>
          View and track your workflow submissions
        </p>
      </div>

      {/* Filters */}
      <Card variant="outlined" style={{ overflow: 'visible' }}>
        <Card.Body>
          <Flex justify="between" align="center" wrap="wrap" gap="md">
            {/* View Mode Toggle (Admin only) */}
            {isAdmin && (
              <Flex align="center" gap="md">
                <span style={{ fontSize: fontSize.sm, color: textColor, fontWeight: 500 }}>
                  View:
                </span>
                <Radio
                  name="viewMode"
                  value="my"
                  checked={viewMode === 'my'}
                  onChange={() => setViewMode('my')}
                  label="My Runs"
                />
                <Radio
                  name="viewMode"
                  value="all"
                  checked={viewMode === 'all'}
                  onChange={() => setViewMode('all')}
                  label="All Runs"
                />
              </Flex>
            )}

            {/* Status Filter */}
            <Flex align="center" gap="sm">
              <span style={{ fontSize: fontSize.sm, color: textColor, fontWeight: 500 }}>
                Status:
              </span>
              <Select
                value={statusFilter}
                onChange={(value: string) => setStatusFilter(value as StatusFilter)}
                options={statusOptions}
              />
            </Flex>

            {/* Results Count */}
            {countLabel && (
              <span style={{ fontSize: fontSize.sm, color: mutedColor }}>
                {countLabel}
              </span>
            )}
          </Flex>
        </Card.Body>
      </Card>

      {/* Error State */}
      {error && (
        <Card variant="outlined">
          <Card.Body>
            <Stack gap="sm">
              <h3 style={{ margin: 0, color: theme.colors.semantic.error.default }}>
                Error Loading History
              </h3>
              <p style={{ margin: 0, color: mutedColor }}>{error.message}</p>
            </Stack>
          </Card.Body>
        </Card>
      )}

      {/* History List */}
      <WorkflowHistoryList
        runs={filteredRuns}
        isLoading={isLoading}
        showUserColumn={viewMode === 'all'}
        onViewDetails={handleViewDetails}
      />

      {/* Load More */}
      {!isLoading && !reachedEnd && loadedRuns.length > 0 && (
        <Flex justify="center">
          <Button
            variant="outline"
            color="primary"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Flex>
      )}

      {/* Help Section */}
      {!isLoading && filteredRuns.length === 0 && !error && (
        <Card variant="outlined">
          <Card.Body>
            <Stack gap="sm">
              <h3 style={{ margin: 0, ...textStyles.bodySmall, fontWeight: textStyles.label.fontWeight, color: textColor }}>
                No Workflows Found
              </h3>
              <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.sm }}>
                {statusFilter !== 'all'
                  ? `No workflows with "${statusFilter}" status in the loaded page${reachedEnd ? '' : '. Load more to keep searching.'}`
                  : !isAdmin
                  ? 'No workflows found for your assigned matters. If you expect to see results, contact an administrator.'
                  : 'You haven\'t submitted any workflows yet. Start by transcribing a video or uploading an audio file.'}
              </p>
            </Stack>
          </Card.Body>
        </Card>
      )}
    </Stack>
  );
}

export default WorkflowHistoryPage;
